import React from 'react';
import { Typography, TextField, Box } from '@mui/material';

// Note: Validation functions are now handled by the parent hook

import type { MixerInfo } from '../../types/company.types';

import { getStylesForEditableField } from '@/utils/helpers';
import { PaperBoxStyles } from '@/components/styles';

interface MixerSectionProps {
  mixerInfo: MixerInfo;
  originalMixerInfo?: MixerInfo;
  isEditMode?: boolean;
  onUpdateField: (field: string, value: string) => void;
  getFieldError: (field: string) => string | undefined;
  isFieldValid: (field: string) => boolean;
}

export const MixerSection: React.FC<MixerSectionProps> = ({
  mixerInfo,
  originalMixerInfo,
  isEditMode,
  onUpdateField,
  getFieldError,
  isFieldValid,
}) => {
  // Note: Validation is now handled by the parent hook to ensure proper touched field checking
  return (
    <PaperBoxStyles elevation={1} sx={{ mb: 2, paddingBottom: '32px' }}>
      <Typography
        variant='h5'
        fontWeight='bold'
        sx={{
          mb: 3,
          color: '#1A1C20',
          fontSize: '22px',
          fontWeight: 600,
        }}
      >
        ミキサ
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Row 1 - 公称能力 */}
        <TextField
          label='公称能力'
          placeholder='公称能力を入力'
          value={mixerInfo.ratedCapacity || ''}
          onChange={(e) => {
            const value = e.target.value;
            onUpdateField('ratedCapacity', value);
          }}
          error={!isFieldValid('mixerInfo.ratedCapacity')}
          helperText={getFieldError('mixerInfo.ratedCapacity')}
          fullWidth
          slotProps={{
            input: {
              inputProps: { maxLength: 50 },
              sx: getStylesForEditableField(
                !!isEditMode,
                originalMixerInfo?.ratedCapacity,
                mixerInfo?.ratedCapacity
              ),
            },
          }}
          variant='outlined'
        />

        {/* Row 2 - 形式 */}
        <TextField
          label='形式'
          placeholder='形式を入力'
          value={mixerInfo.type || ''}
          onChange={(e) => {
            const value = e.target.value;
            onUpdateField('type', value);
          }}
          error={!isFieldValid('mixerInfo.type')}
          helperText={getFieldError('mixerInfo.type')}
          fullWidth
          slotProps={{
            input: {
              inputProps: { maxLength: 50 },
              sx: getStylesForEditableField(!!isEditMode, originalMixerInfo?.type, mixerInfo?.type),
            },
          }}
          variant='outlined'
        />

        {/* Row 3 - 容量・基数 */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
            width: '100%',
          }}
        >
          <TextField
            label='容量'
            placeholder='容量'
            value={mixerInfo.mixerCapacity || ''}
            onChange={(e) => {
              const value = e.target.value;
              onUpdateField('mixerCapacity', value);
            }}
            error={!isFieldValid('mixerInfo.mixerCapacity')}
            helperText={getFieldError('mixerInfo.mixerCapacity')}
            sx={{
              flex: 1,
              minWidth: '200px',
            }}
            slotProps={{
              input: {
                inputProps: { maxLength: 50 },
                sx: getStylesForEditableField(
                  !!isEditMode,
                  originalMixerInfo?.mixerCapacity,
                  mixerInfo?.mixerCapacity
                ),
              },
            }}
            variant='outlined'
            fullWidth
          />
          <Typography
            variant='body1'
            sx={{
              color: '#1A1C20',
              fontWeight: 600,
              fontSize: '16px',
              mx: 0.5,
              flexShrink: 0,
            }}
          >
            ×
          </Typography>
          <TextField
            label='基数'
            placeholder='基数'
            value={mixerInfo.mixerCardinality || ''}
            onChange={(e) => {
              const value = e.target.value;
              onUpdateField('mixerCardinality', value);
            }}
            error={!isFieldValid('mixerInfo.mixerCardinality')}
            helperText={getFieldError('mixerInfo.mixerCardinality')}
            sx={{
              flex: 1,
              minWidth: '200px',
            }}
            slotProps={{
              input: {
                inputProps: { maxLength: 9 },
                sx: getStylesForEditableField(
                  !!isEditMode,
                  originalMixerInfo?.mixerCardinality,
                  mixerInfo?.mixerCardinality
                ),
              },
            }}
            variant='outlined'
            fullWidth
          />
          <Typography
            variant='body2'
            sx={{
              color: '#1A1C20',
              fontWeight: 500,
              fontSize: '14px',
              ml: 0.5,
              flexShrink: 0,
            }}
          >
            基
          </Typography>
        </Box>

        {/* Row 4 - 製作会社 */}
        <TextField
          label='製作会社'
          placeholder='製作会社を入力'
          value={mixerInfo.manufacturer || ''}
          onChange={(e) => {
            const value = e.target.value;
            onUpdateField('manufacturer', value);
          }}
          error={!isFieldValid('mixerInfo.manufacturer')}
          helperText={getFieldError('mixerInfo.manufacturer')}
          fullWidth
          slotProps={{
            input: {
              inputProps: { maxLength: 50 },
              sx: getStylesForEditableField(
                !!isEditMode,
                originalMixerInfo?.manufacturer,
                mixerInfo?.manufacturer
              ),
            },
          }}
          variant='outlined'
        />

        {/* Row 5 - 計量方式 */}
        <TextField
          label='計量方式'
          placeholder='計量方式を入力'
          value={mixerInfo.measurementMethod || ''}
          onChange={(e) => {
            const value = e.target.value;
            onUpdateField('measurementMethod', value);
          }}
          error={!isFieldValid('mixerInfo.measurementMethod')}
          helperText={getFieldError('mixerInfo.measurementMethod')}
          fullWidth
          slotProps={{
            input: {
              inputProps: { maxLength: 50 },
              sx: getStylesForEditableField(
                !!isEditMode,
                originalMixerInfo?.measurementMethod,
                mixerInfo?.measurementMethod
              ),
            },
          }}
          variant='outlined'
        />
      </Box>
    </PaperBoxStyles>
  );
};
